import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useDeleteClientGroupMutation, useGetClientGroupQuery } from '../../api/client-groups-api';
import { clientGroupFormDeleteModalId, clientGroupsAlertId } from '../../constants/constants';
import { selectActiveClientGroupId, setActiveClientGroupId } from '../../store/client-groups-slice';

const ClientGroupDeleteModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['client-groups', 'common']);
  const dispatch = useDispatch();
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();

  const activeClientGroupId = useAppSelector(selectActiveClientGroupId);

  const [deleteClientGroup, { isLoading: isDeleteClientGroupLoading }] = useDeleteClientGroupMutation();

  const { data: clientGroupData } = useGetClientGroupQuery(activeClientGroupId || '', {
    skip: !activeClientGroupId,
  });

  const closeClientGroupDeleteModal = () => {
    dispatch(closeModal(clientGroupFormDeleteModalId));
    dispatch(setActiveClientGroupId(null));
  };

  const handleSubmit = () => {
    resetConfirmationError();

    if (clientGroupData?.id) {
      deleteClientGroup(clientGroupData.id)
        .unwrap()
        .then(() => {
          dispatch(
            setAlert({
              id: clientGroupsAlertId,
              type: 'success',
              title: t('groupDelete.successDeleteMessage'),
            }),
          );
          closeClientGroupDeleteModal();
        })
        .catch(() => addConfirmationError(t('errors.deleteFailed', { ns: 'common' })));
    }
  };

  return (
    <Modal
      title={t('groupDelete.deleteGroup')}
      onCancel={closeClientGroupDeleteModal}
      isLoading={isDeleteClientGroupLoading}
      customSubmitText={t('action.delete', { ns: 'common' })}
      onOk={handleSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      {clientGroupData?.name && (
        <ModalConfirmationContent
          confirmDeleteText={
            <Trans
              i18nKey="groupDelete.confirmationDelete"
              t={t}
              ns="client-groups"
              values={{ name: clientGroupData.name }}
            />
          }
          confirmationError={confirmationError}
        />
      )}
    </Modal>
  );
};

const ClientGroupDeleteModal = (): JSX.Element => {
  return <LazyModal modalId={clientGroupFormDeleteModalId} Component={ClientGroupDeleteModalContent} />;
};

export default ClientGroupDeleteModal;
